import React, {useRef} from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import {draggable, droppable} from '../../../lib/dragAndDrop';
import DragItem from './DragItem';
import {DragDropType, TransformSelectionType} from '../../../constants/dataEnum';
import {filterToArray} from '../../../lib/objectHelper';
import {mergeRefs} from '../../../lib/refHelper';

const Container = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  pointer-events: auto;
`;

const DropZone = React.forwardRef(({drop, children}, ref) => (
  <div
    ref={mergeRefs(drop, ref)}
    style={{
      height: '100%',
      width: '100%',
    }}
  >
    {children}
  </div>
));

DropZone.propTypes = {
  drop: PropTypes.any.isRequired,
  children: PropTypes.any.isRequired,
};

const DropAbleZone = droppable(DropZone);
const DragAbleItem = draggable(DragItem);

function DropZoneContainer({
  dropZoneID,
  row,
  col,
  layouts,
  onDrop,
  onDrag,
  onHover,
  isDisableDrop,
  onResizeStop,
  onResize,
  onResizeStart,
  setDraggedLayout,
  setDraggedHandle,
  setHoveredWell,
  getEnableHandle,
  hoveredWell,
}) {
  const containerRef = useRef(null);

  const renderDragableItem = () => {
    const returnValue = [];
    const oneDimensionLayout = filterToArray(layouts, {type: TransformSelectionType.ONE_DIMENSION});
    const twoDimensionLayouts = filterToArray(layouts, {type: TransformSelectionType.TWO_DIMENSION});
    const rearrangedLayouts = [...twoDimensionLayouts, ...oneDimensionLayout];

    const generatedKey = rearrangedLayouts
      .map((layout) => `${layout.start.row}-${layout.start.column}-${layout.size.row}-${layout.size.column}`)
      .join('-');
    rearrangedLayouts.forEach((layout, key) => {
      returnValue.push(
        <DragAbleItem
          // eslint-disable-next-line react/no-array-index-key
          key={`${layout.cardID}-${key}-${generatedKey}`}
          dropZoneID={dropZoneID}
          itemID={`${dropZoneID}-${layout.cardID}`}
          itemType={DragDropType.TRANSFORM_SELECTION}
          disablePreview
          onDrag={onDrag}
          row={row}
          col={col}
          layout={layout}
          setDraggedLayout={setDraggedLayout}
          setDraggedHandle={setDraggedHandle}
          onResizeStop={onResizeStop}
          onResize={onResize}
          onResizeStart={onResizeStart}
          getEnableHandle={getEnableHandle}
        />,
      );
    });
    return returnValue;
  };

  const calculateWellPosition = (e) => {
    const {clientX: x, clientY: y} = e;

    const {height, width, left, top} = containerRef.current.getBoundingClientRect();
    const wellSize = {height: height / col, width: width / row};
    const targetRow = Math.ceil((width + left - x) / wellSize.width);
    const targetColumn = Math.ceil((y - top) / wellSize.height);

    if (hoveredWell.row === targetRow && hoveredWell.column === targetColumn) {
      return;
    }

    setHoveredWell({column: targetColumn, row: targetRow});
  };

  return (
    <Container
      ref={containerRef}
      row={row}
      col={col}
      onMouseMove={calculateWellPosition}
      onMouseLeave={() => {
        if (hoveredWell.row === null && hoveredWell.column === null) {
          return;
        }

        setHoveredWell({column: null, row: null});
      }}
    >
      <DropAbleZone
        itemID={dropZoneID}
        acceptedItems={[DragDropType.TRANSFORM_SELECTION]}
        isDisableDrop={isDisableDrop}
        onDrop={onDrop}
        directOnHover={onHover}
        row={row}
        col={col}
      >
        {renderDragableItem()}
      </DropAbleZone>
    </Container>
  );
}

DropZoneContainer.propTypes = {
  dropZoneID: PropTypes.number.isRequired,
  layouts: PropTypes.object,
  col: PropTypes.number,
  row: PropTypes.number,
  onDrop: PropTypes.func,
  onDrag: PropTypes.func,
  onHover: PropTypes.func,
  isDisableDrop: PropTypes.func,
  onResizeStart: PropTypes.func,
  onResize: PropTypes.func,
  onResizeStop: PropTypes.func,
  setDraggedLayout: PropTypes.func,
  setDraggedHandle: PropTypes.func,
  setHoveredWell: PropTypes.func,
  getEnableHandle: PropTypes.func,
  hoveredWell: PropTypes.object,
};

DropZoneContainer.defaultProps = {
  onDrop: () => {},
  onDrag: () => {},
  onHover: () => {},
  isDisableDrop: () => {},
  onResizeStart: () => {},
  onResize: () => {},
  onResizeStop: () => {},
  setDraggedLayout: () => {},
  setDraggedHandle: () => {},
  setHoveredWell: () => {},
  getEnableHandle: () => {},
  hoveredWell: {},
  col: 0,
  row: 0,
  layouts: {},
};

export default DropZoneContainer;
